import * as React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Fab,
  useTheme,
  ListItemButton,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useModalAddOperation } from '../hooks/useModalAddOperation';
import ModalOperations, { OperationData } from '../modal/modalAddOperation';
import { useAuth } from '../../provider/AuthProvider';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { PwaInstallDialog } from '../pwaInstallDialog';
import HeaderSearch from '../headerSearch';
import { getOperationFromSearch, globalSearch, SearchItem } from '../../api/searchApi';
import { formatCurrency } from '../../utils/formatMoney';
import { SEARCH_LIMITS } from '../../utils/validationLimits';

const pages = [
  { path: '/operations', name: 'Операции' },
  { path: '/settings', name: 'Настройки' },
];

export default function Header() {
  const {
    openAddOperation,
    handleCloseAddOperation,
    handleOpenAddOperation,
    editingItem,
    setEditingItem,
  } = useModalAddOperation<OperationData>();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { ios, canPrompt, promptInstall, installed } = usePwaInstall();
  const [pwaHelpOpen, setPwaHelpOpen] = React.useState(false);

  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [search, setSearch] = React.useState('');
  const [openSearch, setOpenSearch] = React.useState(false);
  const [searchActive, setSearchActive] = React.useState(false);

  const closeSearch = () => {
    setOpenSearch(false);
    setSearchActive(false);
    setSearch('');
  };

  const deferredSearch = React.useDeferredValue(search);
  const query = deferredSearch.trim().slice(0, SEARCH_LIMITS.max);

  const { data = [], isLoading } = useQuery<SearchItem[], Error>({
    queryKey: ['global-search', query],
    enabled: query.length >= 2,
    queryFn: () => globalSearch({ q: query, limit: 8 }),
  });

  const showDropdown = openSearch && search.trim().length > 0;

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleInstallClick = async () => {
    if (installed) return;
    if (canPrompt) {
      await promptInstall();
      return;
    }
    setPwaHelpOpen(true);
  };

  const handleSelectItem = async (item: SearchItem) => {
    setOpenSearch(false);
    setSearchActive(false);
    setSearch('');

    if (item.kind !== 'operation') {
      navigate(item.href);
      return;
    }

    try {
      const fullOperation = await getOperationFromSearch(item);
      handleOpenAddOperation(fullOperation);
    } finally {
    }
  };

  const handleSearchSubmit = () => {
    if (query.length < 2) {
      setOpenSearch(true);
      return;
    }

    if (data.length > 0) {
      handleSelectItem(data[0]);
      return;
    }

    setOpenSearch(true);
  };

  const getIcon = (item: SearchItem) => {
    if (item.kind === 'tag') return <SellOutlinedIcon fontSize="small" />;
    if (item.kind === 'category') return <CategoryOutlinedIcon fontSize="small" />;
    return <ReceiptLongOutlinedIcon fontSize="small" />;
  };

  const getMeta = (item: SearchItem) => {
    if (item.kind === 'tag') {
      return item.type === 'income' ? 'Тег дохода' : 'Тег расхода';
    }

    if (item.kind === 'category') {
      return item.type === 'income' ? 'Категория дохода' : 'Категория расхода';
    }

    const parts = [
      item.type === 'income' ? 'Доход' : 'Расход',
      item.amount ? formatCurrency(item.amount) : '',
      item.date ? new Date(item.date).toLocaleString('ru-RU') : '',
    ].filter(Boolean);

    return parts.join(' • ');
  };

  const renderSearchResults = () => (
    <>
      {query.length < 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Введите минимум 2 символа
          </Typography>
        </Box>
      )}

      {query.length >= 2 && isLoading && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {query.length >= 2 && !isLoading && data.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Ничего не найдено
          </Typography>
        </Box>
      )}

      {query.length >= 2 &&
        !isLoading &&
        data.map((item, index) => (
          <React.Fragment key={`${item.kind}-${item.type}-${item.id}`}>
            <ListItemButton onClick={() => handleSelectItem(item)} sx={{ py: 1.25 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  mr: 1.5,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor:
                    item.type === 'income' ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)',
                  color: item.type === 'income' ? 'success.main' : 'error.main',
                  flexShrink: 0,
                }}
              >
                {getIcon(item)}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {getMeta(item)}
                </Typography>
              </Box>
            </ListItemButton>

            {index < data.length - 1 && <Divider />}
          </React.Fragment>
        ))}
    </>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#fff' }}>
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link to="/">
              <img src="/logo.png" alt="Logo" style={{ width: '100px' }} />
            </Link>

            <IconButton onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <IconButton
              onClick={() => {
                setSearchActive(true);
                setOpenSearch(true);
              }}
              aria-label="open search"
              sx={{
                width: 44,
                height: 44,
                border: '1px solid rgba(0,0,0,0.12)',
                bgcolor: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <SearchIcon />
            </IconButton>

            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleOpenAddOperation()}
              size="medium"
            >
              <AddIcon />
            </Fab>
          </Box>
        </Toolbar>
      </AppBar>

      {searchActive && (
        <>
          <Box
            onClick={closeSearch}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.45)',
              zIndex: 2000,
            }}
          />

          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 2001,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              pt: { xs: 7, sm: 8 },
              px: 2,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 520,
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              <HeaderSearch
                value={search}
                autoFocus
                onChange={value => {
                  const nextValue = value.slice(0, SEARCH_LIMITS.max);
                  setSearch(nextValue);
                  setOpenSearch(nextValue.trim().length > 0);
                }}
                onFocus={() => {
                  if (search.trim().length > 0) setOpenSearch(true);
                }}
                onSubmit={handleSearchSubmit}
                placeholder="Поиск операций..."
                width="100%"
              />

              {showDropdown && (
                <Paper
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    borderRadius: 3,
                    overflow: 'hidden',
                    zIndex: 2002,
                  }}
                >
                  {renderSearchResults()}
                </Paper>
              )}
            </Box>
          </Box>
        </>
      )}

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { zIndex: theme.zIndex.drawer + 2 } }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {pages.map(page => (
              <ListItem button key={page.name}>
                <Link
                  to={page.path}
                  style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
                >
                  <ListItemText primary={page.name} />
                </Link>
              </ListItem>
            ))}
          </List>

          <Divider />

          <ListItem disablePadding>
            <ListItemButton onClick={handleInstallClick} disabled={installed}>
              <ListItemText
                primary={installed ? 'Приложение установлено ✅' : 'Установить приложение'}
                secondary={
                  installed
                    ? undefined
                    : canPrompt
                      ? 'Нажми, чтобы установить'
                      : ios
                        ? 'iPhone: через “Поделиться”'
                        : 'Откроем инструкцию'
                }
              />
            </ListItemButton>
          </ListItem>

          <List>
            <ListItem button onClick={logout}>
              <Link to="/exit" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                <ListItemText primary="Выход" />
              </Link>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <PwaInstallDialog open={pwaHelpOpen} onClose={() => setPwaHelpOpen(false)} />
      <ModalOperations
        typeItem={editingItem?.type}
        open={openAddOperation}
        initialData={editingItem ?? undefined}
        onClose={() => {
          handleCloseAddOperation();
          setEditingItem(null);
        }}
        onSubmit={() => {
          handleCloseAddOperation();
          setEditingItem(null);
        }}
      />
    </>
  );
}
